import React, { useCallback, useEffect, useState } from 'react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Search, KeyRound, Smartphone } from 'lucide-react';
import { usePermissions } from '@/contexts/PermissionsContext';
import { startImpersonation } from '@/lib/impersonation';

function groupLabel(dateStr) {
  if (!dateStr) return 'Unbekannt';
  const d = new Date(dateStr);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days < 7) return 'Letzte Woche';
  if (days < 14) return 'Vor 2 Wochen';
  if (days < 21) return 'Vor 3 Wochen';
  if (days < 28) return 'Vor 4 Wochen';
  if (days < 60) return 'Vor einem Monat';
  return 'Älter';
}

const AREA_LABEL = { lehrer: 'Lehrer', schueler: 'Schüler', eltern: 'Eltern' };

export default function AppKontenTab() {
  const { isSuperAdmin } = usePermissions();
  const [accounts, setAccounts] = useState([]);
  const [search, setSearch] = useState('');
  const [openingId, setOpeningId] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/admin/app-verwaltung/accounts', { params: { search: search || undefined } });
      setAccounts(r.data || []);
    } catch (e) { toast.error('App-Konten konnten nicht geladen werden'); }
  }, [search]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const openCompanion = async (a) => {
    if (!a?.user_id || !a.is_active) return;
    setOpeningId(a.user_id);
    try {
      await startImpersonation(a.user_id, { to: '/m' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Companion-Öffnen fehlgeschlagen');
      setOpeningId(null);
    }
  };

  const groups = {};
  accounts.forEach((a) => {
    const g = groupLabel(a.registered_at);
    (groups[g] = groups[g] || []).push(a);
  });

  const fmt = (d) => d ? new Date(d).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="max-w-5xl mx-auto">
        <div className="relative max-w-sm mb-4">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <Input className="pl-9 bg-white" placeholder="Schnellsuche (Name, E-Mail)…" value={search}
                 onChange={(e) => setSearch(e.target.value)} data-testid="app-konten-search" />
        </div>
        {isSuperAdmin && (
          <p className="text-xs text-slate-500 mb-3" data-testid="app-konten-companion-hint">
            Als Super-Admin: „Companion öffnen“ wechselt in das App-Konto (Kontowechsel, Banner „Zurück“).
          </p>
        )}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                <th className="p-3 text-left">Registrierung</th>
                <th className="p-3 text-left">Name</th>
                <th className="p-3 text-left">E-Mail</th>
                <th className="p-3 text-left">Kennungen</th>
                <th className="p-3 text-left">Status</th>
                {isSuperAdmin && <th className="p-3 text-left">Companion</th>}
              </tr>
            </thead>
            <tbody>
              {Object.entries(groups).map(([g, list]) => (
                <React.Fragment key={g}>
                  <tr className="bg-slate-100">
                    <td colSpan={isSuperAdmin ? 6 : 5} className="px-3 py-1.5 text-xs font-semibold text-slate-500">{g}</td>
                  </tr>
                  {list.map((a) => (
                    <tr key={a.user_id} className="border-t border-slate-100" data-testid={`app-konto-row-${a.user_id}`}>
                      <td className="p-3 text-slate-500 whitespace-nowrap">{fmt(a.registered_at)}</td>
                      <td className="p-3 font-medium">{a.full_name || '—'}</td>
                      <td className="p-3 text-slate-600">{a.email || '—'}</td>
                      <td className="p-3">
                        <span className="inline-flex items-center gap-1.5 flex-wrap">
                          <KeyRound className="w-3.5 h-3.5 text-brand-red" />
                          {a.identity_count}
                          {a.identity_names.length > 0 && (
                            <span className="text-xs text-slate-400">({a.identity_names.join(', ')})</span>
                          )}
                          {(a.identity_areas || []).map((area) => (
                            <Badge key={area} variant="outline" className="text-[10px] font-normal">
                              {AREA_LABEL[area] || area}
                            </Badge>
                          ))}
                        </span>
                      </td>
                      <td className="p-3">
                        {a.is_active
                          ? <Badge className="bg-brand-success/10 text-brand-success hover:bg-brand-success/10">Aktiv</Badge>
                          : <Badge variant="destructive">Deaktiviert</Badge>}
                      </td>
                      {isSuperAdmin && (
                        <td className="p-3">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={!a.is_active || openingId === a.user_id}
                            onClick={() => openCompanion(a)}
                            data-testid={`app-konto-open-companion-${a.user_id}`}
                            className="gap-1.5"
                          >
                            <Smartphone className="w-3.5 h-3.5" />
                            {openingId === a.user_id ? 'Öffne…' : 'Öffnen'}
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
              {!accounts.length && (
                <tr><td colSpan={isSuperAdmin ? 6 : 5} className="p-10 text-center text-slate-400">Noch keine App-Konten registriert</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-400 mt-2">Gefunden: {accounts.length}</p>
      </div>
    </div>
  );
}
